import {
    buttonBaseClasses,
    checkboxClasses,
    chipClasses,
    type Components,
    paginationItemClasses,
    svgIconClasses,
    tableCellClasses,
    tableRowClasses,
    tableSortLabelClasses,
    type Theme,
    typographyClasses,
} from '@mui/material'

import { errorMessagesClasses } from '../ErrorMessage/Classes'
import { BUTTON_DISABLED_OPACITY, iconButtonFocusRing, insetFocusOutline, secondaryButtonStyle } from './buttons'
import { ChevronDownIcon } from './icons'

export const HEADER_ROW_HEIGHT = 36
export const CONTAINER_BORDER_WIDTH = 1
export const CHECKBOX_COLUMN_WIDTH = 40
const SELECTED_ROW_BAR_WIDTH = 2
/** In spacing steps: (24px medium chip - 20px text line) / 2 */
const CHIP_LINE_OVERHANG = 0.5
const ERROR_ICON_SIZE = 16
const SORT_ICON_SIZE = 12
const SORT_ICON_HOVER_OPACITY = 0.45
export const SORT_LABEL_HOVER_FONT_WEIGHT = 600
const PAGE_ITEM_SIZE = 32
const PAGE_PREVIOUS_NEXT_SIZE = 28
const PAGE_ITEM_GAP = 3.5
const PAGE_ICON_SIZE = 16

export const MuiTableContainer: Components<Theme>['MuiTableContainer'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            border: `${CONTAINER_BORDER_WIDTH}px solid ${theme.palette.grey[200]}`,
            borderRadius: 8,
            backgroundColor: theme.palette.background.default,
        }),
    },
}

export const MuiTableRow: Components<Theme>['MuiTableRow'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            [`&:last-child > .${tableCellClasses.body}`]: {
                borderBottom: 'none',
            },
            [`&.${tableRowClasses.hover}:hover`]: {
                backgroundColor: theme.palette.grey[50],
            },
            [`&.${tableRowClasses.selected}, &.${tableRowClasses.selected}:hover`]: {
                backgroundColor: theme.palette.primary[50],
            },
            /** Drawn on the first cell, since browsers don't reliably paint a box-shadow on a table row */
            [`&.${tableRowClasses.selected} > .${tableCellClasses.root}:first-of-type`]: {
                boxShadow: `inset ${SELECTED_ROW_BAR_WIDTH}px 0 0 0 ${theme.palette.primary[700]}`,
            },
            '&:focus-visible': insetFocusOutline(theme),
        }),
        head: {
            height: HEADER_ROW_HEIGHT,
        },
    },
}

export const MuiTableCell: Components<Theme>['MuiTableCell'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.body2,
            color: theme.palette.grey[900],
            /**
             * 6px + a 20px line + 5px + the 1px divider keeps a single-line row at 32px, while taller rows grow and stay top-aligned.
             * The last row's line is the container's own border instead.
             */
            padding: theme.spacing(1.5, 3, 1.25),
            verticalAlign: 'top',
            borderBottom: `1px solid ${theme.palette.grey[200]}`,

            /**
             * A 24px chip (e.g. `Status`) in the cell's 20px text line: top-aligned and pulled 2px into the cell padding above and below,
             * so it centres on the line without making the row taller
             */
            [`& > .${chipClasses.root}.${chipClasses.sizeMedium}`]: {
                verticalAlign: 'top',
                marginTop: theme.spacing(-CHIP_LINE_OVERHANG),
                marginBottom: theme.spacing(-CHIP_LINE_OVERHANG),
            },
            /** An error message is page-sized by default; in a cell it takes the cell's text size, so an error row stays as tall as the others */
            [`& .${errorMessagesClasses.root}`]: {
                [`& .${typographyClasses.root}`]: theme.typography.body2,
                [`& .${svgIconClasses.root}`]: { fontSize: ERROR_ICON_SIZE },
            },
        }),
        head: ({ theme }) => ({
            ...theme.typography.caption,
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.grey[600],
            backgroundColor: theme.palette.background.default,
            padding: theme.spacing(0, 3),
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            borderBottomColor: theme.palette.grey[300],

            /** The sort label fills a sortable header cell, so the whole cell is its hover and click target */
            [`&:has(> .${tableSortLabelClasses.root})`]: {
                padding: 0,
            },
        }),
        paddingCheckbox: ({ theme }) => ({
            boxSizing: 'border-box',
            width: CHECKBOX_COLUMN_WIDTH,
            /** 8px down, the 16px checkbox centers on the other cells' first 20px text line */
            padding: theme.spacing(2, 3, 0),
            /** Block-level, so it sits on the padding edge instead of being aligned to a text baseline */
            [`& > .${checkboxClasses.root}`]: {
                display: 'flex',
            },

            [`&.${tableCellClasses.head}`]: {
                paddingTop: 0,
            },
        }),
    },
}

export const MuiTableSortLabel: Components<Theme>['MuiTableSortLabel'] = {
    defaultProps: {
        IconComponent: ChevronDownIcon,
    },
    styleOverrides: {
        root: ({ theme }) => ({
            width: '100%',
            height: HEADER_ROW_HEIGHT,
            padding: theme.spacing(0, 3),
            color: 'inherit',

            [`&:hover, &:focus, &.${tableSortLabelClasses.active}`]: {
                color: 'inherit',
            },
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.grey[700],
                fontWeight: SORT_LABEL_HOVER_FONT_WEIGHT,

                [`& .${tableSortLabelClasses.icon}`]: {
                    opacity: SORT_ICON_HOVER_OPACITY,
                },
            },
            [`&.${tableSortLabelClasses.active} .${tableSortLabelClasses.icon}`]: {
                opacity: 1,
                color: 'inherit',
            },
            /** An idle sortable column gets `direction="asc"`, but its hover hint should point down, unrotated */
            [`&:not(.${tableSortLabelClasses.active}) .${tableSortLabelClasses.icon}`]: {
                transform: 'none',
            },
            [`&.${buttonBaseClasses.focusVisible}`]: insetFocusOutline(theme),
        }),
        icon: ({ theme }) => ({
            fontSize: SORT_ICON_SIZE,
            margin: theme.spacing(0, 0, 0, 1),

            /** A right-aligned cell reverses the label, putting the icon before the text, so the gap moves to the icon's other side */
            [`.${tableCellClasses.alignRight} &`]: {
                margin: theme.spacing(0, 1, 0, 0),
            },
        }),
    },
}

export const MuiPagination: Components<Theme>['MuiPagination'] = {
    styleOverrides: {
        ul: ({ theme }) => ({
            gap: theme.spacing(PAGE_ITEM_GAP),
        }),
    },
}

export const MuiPaginationItem: Components<Theme>['MuiPaginationItem'] = {
    styleOverrides: {
        root: ({ theme }) => ({
            ...theme.typography.caption,
            fontVariantNumeric: 'tabular-nums',
            color: theme.palette.grey[900],
            minWidth: PAGE_ITEM_SIZE,
            height: PAGE_ITEM_SIZE,
            margin: 0,
            padding: 0,
            borderRadius: 0,
        }),
        page: ({ theme }) => ({
            /** Transparent at rest, so marking the current page never changes the item's height */
            borderBottom: '1px solid transparent',

            '&:hover': {
                backgroundColor: 'transparent',
                color: theme.palette.primary[700],
            },
            [`&.${paginationItemClasses.selected}, &.${paginationItemClasses.selected}:hover`]: {
                backgroundColor: 'transparent',
                color: theme.palette.primary[700],
                borderBottomColor: theme.palette.primary[700],
            },
            [`&.${paginationItemClasses.focusVisible}, &.${paginationItemClasses.selected}.${paginationItemClasses.focusVisible}`]: {
                backgroundColor: 'transparent',
                ...insetFocusOutline(theme),
                borderRadius: 4,
            },
        }),
        ellipsis: {
            height: 'auto',
        },
        icon: {
            fontSize: PAGE_ICON_SIZE,
            margin: 0,
        },
        previousNext: ({ theme }) => ({
            ...secondaryButtonStyle(theme),
            minWidth: PAGE_PREVIOUS_NEXT_SIZE,
            width: PAGE_PREVIOUS_NEXT_SIZE,
            height: PAGE_PREVIOUS_NEXT_SIZE,
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: 4,

            [`&.${paginationItemClasses.focusVisible}`]: iconButtonFocusRing(theme),
            /** Undoes MUI's focus-visible background at rest only, so a focused button still shows its hover and active backgrounds */
            [`&.${paginationItemClasses.focusVisible}:not(:hover):not(:active)`]: {
                backgroundColor: theme.palette.common.white,
            },
            [`&.${paginationItemClasses.disabled}`]: {
                opacity: BUTTON_DISABLED_OPACITY,
            },
        }),
    },
}
